import { CSSProperties, PropsWithChildren, useMemo } from 'react';
import { OutlineArrowUpDownIcon, SolidPolygonArrowDownIcon, SolidPolygonArrowUpIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Row } from '../Row';

type TVariant = 'default' | 'primary';

export type TSortValue = {
  direction: 'ASC' | 'DESC' | string;
  field: string;
};

interface ISortingArrowProps {
  name: string;
  sorting: TSortValue;
  style?: CSSProperties;
  toggleOnly?: boolean;
  variant?: TVariant;
  onChange?(value: TSortValue | undefined): void;
}

const VariantColors: Partial<Record<TVariant, { Active: string; InActive: string }>> = {
  primary: {
    Active: colors.primary[500],
    InActive: colors.primary[500],
  },
};

export const SortingArrow = ({
  children,
  name,
  sorting,
  style,
  toggleOnly = false,
  variant = 'default',
  onChange,
}: PropsWithChildren<ISortingArrowProps>) => {
  const ArrowComponent = useMemo(() => {
    const isInactive = name !== sorting.field;

    if (isInactive) {
      return <OutlineArrowUpDownIcon color={VariantColors[variant]?.InActive} size={10} />;
    }

    if (sorting.direction === 'ASC') {
      return <SolidPolygonArrowUpIcon color={VariantColors[variant]?.Active} size={10} />;
    }

    return <SolidPolygonArrowDownIcon color={VariantColors[variant]?.Active} size={10} />;
  }, [name, sorting.direction, sorting.field, variant]);

  const handleSortChanges = () => {
    const isActive = sorting.field === name;

    if (!isActive) {
      // INACTIVE → DESC (first click)
      onChange?.({
        field: name,
        direction: 'DESC',
      });
    } else if (sorting.direction === 'DESC') {
      // DESC → ASC (second click)
      onChange?.({
        field: name,
        direction: 'ASC',
      });
    } else {
      // ASC → INACTIVE (third click) or ASC → DESC (if toggleOnly)
      if (toggleOnly) {
        onChange?.({
          field: name,
          direction: 'DESC',
        });
      } else {
        onChange?.(undefined);
      }
    }
  };

  return (
    <Row
      alignItems="center"
      cursor="pointer"
      gap={4}
      role="button"
      style={{
        ...style,
        justifyContent: style?.justifyContent || 'center',
      }}
      onClick={handleSortChanges}
    >
      {children}
      {ArrowComponent}
    </Row>
  );
};
